import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  BadgeCheck,
  Camera,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Mic,
  Pencil,
  ShieldCheck,
  Square,
  Trash2,
  Truck,
  UserPlus,
  X,
  AlertCircle,
} from "lucide-react";

import api from "../api";
import { isImageFile, uploadErrorMessage } from "../uploadUtils";

const MAX_FILE_MB = 25;
const MAX_PHOTOS = 10;
const MAX_VOICE = 10;
const MAX_RECORD_SECONDS = 120;

const tooBig = (file) => file.size > MAX_FILE_MB * 1024 * 1024;
const cleanMobile = (value = "") => value.replace(/[^\d+]/g, "");
const formatSeconds = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const pickAudioType = () => {
  if (typeof MediaRecorder === "undefined") return "";

  return (
    [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg",
    ].find((type) => MediaRecorder.isTypeSupported?.(type)) || ""
  );
};

const audioExtension = (type = "") => {
  if (type.includes("webm")) return "webm";
  if (type.includes("mp4")) return "m4a";
  if (type.includes("ogg")) return "ogg";
  return "webm";
};

export default function UserPage() {
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  const loggedIn = Boolean(storedUser && localStorage.getItem("token"));

  // Step 1
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");

  // Step 2
  const [manualOpen, setManualOpen] = useState(false);
  const [idType, setIdType] = useState("VIN");
  const [idValue, setIdValue] = useState("");
  const [vehicleImage, setVehicleImage] = useState(null);
  const [vehiclePreview, setVehiclePreview] = useState("");
  const [dragging, setDragging] = useState(false);
  const vehicleInputRef = useRef(null);

  // Step 3
  const [partPhotos, setPartPhotos] = useState([]);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const partInputRef = useRef(null);

  // Voice
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [voiceNotes, setVoiceNotes] = useState([]);
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);

      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }

      audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  if (loggedIn) {
    return (
      <Navigate
        to={
          ["admin", "worker"].includes(storedUser?.role)
            ? "/admin/dashboard"
            : "/quotes"
        }
        replace
      />
    );
  }

  const handleVehicleFile = (file) => {
    if (!file) return;

    if (!isImageFile(file)) {
      setError("Vehicle photo must be an image.");
      return;
    }

    if (tooBig(file)) {
      setError(`Vehicle photo is larger than ${MAX_FILE_MB} MB.`);
      return;
    }

    setError("");

    if (vehiclePreview) {
      URL.revokeObjectURL(vehiclePreview);
    }

    setVehicleImage(file);
    setVehiclePreview(URL.createObjectURL(file));
  };

  const clearVehicle = () => {
    if (vehiclePreview) {
      URL.revokeObjectURL(vehiclePreview);
    }

    setVehicleImage(null);
    setVehiclePreview("");
  };

  const handlePartFiles = (fileList) => {
    const images = Array.from(fileList || []).filter(isImageFile);

    const allowed = images.filter((file) => !tooBig(file));
    const remainingSlots = MAX_PHOTOS - partPhotos.length;
    const filesToAdd = allowed.slice(0, Math.max(0, remainingSlots));

    if (allowed.length < images.length) {
      setError(
        `Some photos were larger than ${MAX_FILE_MB} MB and were skipped.`
      );
    } else if (allowed.length > filesToAdd.length) {
      setError(`You can add up to ${MAX_PHOTOS} part photos.`);
    } else {
      setError("");
    }

    setPartPhotos((current) => [
      ...current,
      ...filesToAdd.map((file) => ({
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
  };

  const removePartPhoto = (index) => {
    setPartPhotos((current) => {
      const selected = current[index];
      if (selected?.url) URL.revokeObjectURL(selected.url);
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      stopRecording();
      return;
    }

    if (voiceNotes.length >= MAX_VOICE) {
      setError(`You can add up to ${MAX_VOICE} voice notes.`);
      return;
    }

    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setError(
        "Voice recording is not supported in this browser. Add photos or type a note instead."
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mimeType = pickAudioType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });

        if (blob.size > 0) {
          setVoiceNotes((current) => [
            ...current,
            {
              blob,
              type,
              url: URL.createObjectURL(blob),
            },
          ]);
        }

        stream.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
        setRecording(false);
        setRecordSecs(0);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();

      setError("");
      setRecording(true);
      setRecordSecs(0);

      timerRef.current = setInterval(() => {
        setRecordSecs((current) => {
          const next = current + 1;

          if (next >= MAX_RECORD_SECONDS) {
            setTimeout(stopRecording, 0);
          }

          return next;
        });
      }, 1000);
    } catch (recordingError) {
      console.error(recordingError);
      setError(
        "Microphone access was blocked. Allow microphone access, or add photos / notes instead."
      );
    }
  };

  const removeVoice = (index) => {
    setVoiceNotes((current) => {
      const selected = current[index];
      if (selected?.url) URL.revokeObjectURL(selected.url);
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const validate = () => {
    if (!customerName.trim()) return "Enter your name.";

    const contact = customerContact.trim();
    if (!contact) return "Enter your mobile number or email.";

    if (contact.includes("@")) {
      if (!/\S+@\S+\.\S+/.test(contact)) {
        return "Enter a valid email address.";
      }
    } else {
      const phone = cleanMobile(contact);
      if (phone.replace("+", "").length < 8) {
        return "Enter a valid mobile number.";
      }
    }

    if (!vehicleImage && !idValue.trim()) {
      return "Add a VIN / registration photo, or enter the VIN or Rego manually.";
    }

    if (!notes.trim() && partPhotos.length === 0 && voiceNotes.length === 0) {
      return "Tell us which part you need using voice, photos, or a note.";
    }

    if (recording) return "Stop the voice recording before sending.";

    return "";
  };

  const submit = async () => {
    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    const formData = new FormData();
    const contact = customerContact.trim();

    formData.append("customerName", customerName.trim());
    formData.append(
      "customerContact",
      contact.includes("@") ? contact : cleanMobile(contact)
    );
    formData.append("vehicleMethod", vehicleImage ? "photo" : "manual");

    if (idValue.trim()) {
      const cleanId = idValue.trim();
      formData.append("idType", idType);
      formData.append("idValue", cleanId);
      formData.append(idType === "VIN" ? "vin" : "rego", cleanId);
    }

    formData.append("notes", notes.trim());

    if (vehicleImage) {
      formData.append("vehicleImage", vehicleImage);
    }

    partPhotos.forEach(({ file }) => {
      formData.append("partPhotos", file);
    });

    voiceNotes.forEach((voice, index) => {
      formData.append(
        "voiceNotes",
        voice.blob,
        `voice-${index + 1}.${audioExtension(voice.type)}`
      );
    });

    try {
      setSubmitting(true);
      setError("");

      const { data } = await api.post("/requests/guest", formData, {
        timeout: 120000,
      });

      setDone(data?.request || {});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (submitError) {
      console.error(submitError);

      setError(uploadErrorMessage(submitError, "Could not send your request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const startOver = () => {
    clearVehicle();

    partPhotos.forEach(({ url }) => URL.revokeObjectURL(url));
    voiceNotes.forEach(({ url }) => URL.revokeObjectURL(url));

    setPartPhotos([]);
    setVoiceNotes([]);
    setNotes("");
    setShowNotes(false);
    setIdValue("");
    setManualOpen(false);
    setError("");
    setDone(null);
  };

  return (
    <main className="min-h-screen bg-[#f7f7f7] text-[#131b2f]">
      {/* Top hero */}
      <section className="relative h-[220px] overflow-hidden bg-img md:h-[235px]">
        <div className="relative mx-auto w-full max-w-[1280px] px-5 pt-12 sm:px-8 lg:px-9">
          <div className="absolute right-5 top-5 z-20 flex gap-2 sm:right-8 lg:right-9">
            <Link to="/user/login" className="rounded-lg border border-white/30 bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm transition hover:bg-white/20">User Login</Link>
            <Link to="/admin/login" className="rounded-lg border border-[#ff2d30] bg-[#ff2d30] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#e8212b]">Admin Login</Link>
          </div>
          <h1 className="text-[33px] font-extrabold leading-none tracking-[-0.03em] text-white sm:text-[42px] md:text-[45px]">
            We make it <span className="text-[#ff242b]">Easy.</span>
          </h1>
          <p className="mt-4 text-base font-medium text-white/90 sm:text-lg">
            Need Used Auto Parts?
          </p>
        </div>
      </section>

      {/* Main card */}
      <section className="relative z-10 mx-auto -mt-[31px] w-[calc(100%-32px)] max-w-[1280px] pb-12 sm:w-[calc(100%-48px)] lg:-mt-[30px]">
        <div className="overflow-hidden rounded-[18px] bg-white shadow-[0_20px_70px_rgba(0,0,0,0.09)]">
          {done ? (
            <SuccessPanel
              request={done}
              name={customerName}
              contact={customerContact}
              onAnother={startOver}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 px-6 pb-8 pt-10 md:px-10 lg:grid-cols-3 lg:px-16 lg:pb-9 lg:pt-16 xl:px-20">
                {/* Step 1 */}
                <section className="lg:min-h-[315px] lg:border-r lg:border-[#eceef1] lg:pr-16 xl:pr-20">
                  <StepHeader
                    number="1"
                    title="Personal Details"
                    tone="green"
                  />

                  <div className="mt-9 max-w-[355px] space-y-5">
                    <label className="block">
                      <span className="mb-2 block text-[13px] font-medium text-[#60646d]">
                        Your Name
                      </span>
                      <input
                        type="text"
                        autoComplete="name"
                        value={customerName}
                        onChange={(event) => setCustomerName(event.target.value)}
                        placeholder="Your Name"
                        className="h-[50px] w-full rounded-[9px] border border-transparent bg-[#f7f7f8] px-5 text-sm text-[#202431] outline-none transition placeholder:text-[#b9bcc2] focus:border-[#dce4ef] focus:bg-white focus:ring-4 focus:ring-blue-50"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-[13px] font-medium text-[#60646d]">
                        Mobile Number
                      </span>
                      <input
                        type="text"
                        inputMode="tel"
                        autoComplete="tel"
                        value={customerContact}
                        onChange={(event) => setCustomerContact(event.target.value)}
                        placeholder="Mobile Number"
                        className="h-[50px] w-full rounded-[9px] border border-transparent bg-[#f7f7f8] px-5 text-sm text-[#202431] outline-none transition placeholder:text-[#b9bcc2] focus:border-[#dce4ef] focus:bg-white focus:ring-4 focus:ring-blue-50"
                      />
                    </label>

                    <p className="flex items-center gap-2 text-[13px] text-[#6d7078]">
                      <UserPlus size={17} className="shrink-0 text-[#ff2d30]" />
                      <span>
                        <Link
                          to="/Register"
                          className="font-medium text-[#ff2d30] hover:underline"
                        >
                          Sign up
                        </Link>{" "}
                        and never need to add details again
                      </span>
                    </p>
                    <p className="flex items-center gap-2 text-[13px] text-[#6d7078]">
                      <UserPlus size={17} className="shrink-0 text-[green]" />
                      <span>
                        <Link
                          to="/user/login"
                          className="font-medium text-[green] hover:underline"
                        >
                          Sign in
                        </Link>{" "}
                        Old Account User.
                      </span>
                    </p>
                  </div>
                </section>

                {/* Step 2 */}
                <section className="mt-10 border-t border-[#eceef1] pt-9 lg:mt-0 lg:min-h-[315px] lg:border-r lg:border-t-0 lg:px-16 lg:pt-0 xl:px-20">
                  <StepHeader number="2" title="Which Vehicle?" tone="blue" />

                  <div className="mt-8">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => vehicleInputRef.current?.click()}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          vehicleInputRef.current?.click();
                        }
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setDragging(true);
                      }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={(event) => {
                        event.preventDefault();
                        setDragging(false);
                        handleVehicleFile(event.dataTransfer.files?.[0]);
                      }}
                      className={`flex min-h-[150px] cursor-pointer flex-col items-center justify-center rounded-[10px] border border-dashed px-5 py-5 text-center transition ${dragging
                          ? "border-[#1789f7] bg-[#edf6ff]"
                          : "border-[#8ecaff] bg-[#fbfdff] hover:bg-[#f6fbff]"
                        }`}
                    >
                      {vehiclePreview ? (
                        <div className="relative">
                          <img
                            src={vehiclePreview}
                            alt="Vehicle identification"
                            className="mx-auto h-[92px] max-w-[240px] rounded-lg object-contain"
                          />
                          <button
                            type="button"
                            aria-label="Remove vehicle photo"
                            onClick={(event) => {
                              event.stopPropagation();
                              clearVehicle();
                            }}
                            className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-[#182035] text-white shadow"
                          >
                            <X size={13} />
                          </button>
                          <p className="mt-2 text-[11px] font-semibold text-[#1183ef]">
                            Click to replace image
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="relative grid h-[52px] w-[52px] place-items-center rounded-full bg-[#dcebff] p-2">
                            <div className="grid h-10 w-10 place-items-center rounded-full bg-[#1287ee] text-white shadow-[0_0_0_5px_rgba(18,135,238,0.12)]">
                              <Camera size={18} />
                            </div>
                          </div>
                          <p className="mt-4 text-[13px] font-bold leading-[1.35] text-[#111a31]">
                            Take a picture of your VIN plate /
                            <br />
                            Registration
                          </p>
                          <p className="mt-1 text-[10px] text-[#8b8f97]">
                            Fast way, snap it, get it done.
                          </p>
                        </>
                      )}
                    </div>

                    <input
                      ref={vehicleInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => {
                        handleVehicleFile(event.target.files?.[0]);
                        event.target.value = "";
                      }}
                    />

                    <div className="my-4 flex items-center gap-3">
                      <span className="h-px flex-1 bg-[#eceef1]" />
                      <span className="text-[9px] font-bold uppercase text-[#9297a0]">
                        OR
                      </span>
                      <span className="h-px flex-1 bg-[#eceef1]" />
                    </div>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setManualOpen((current) => !current)}
                        aria-expanded={manualOpen}
                        className="inline-flex items-center gap-1 text-[10px] font-bold text-[#087df1] hover:text-[#0067ca]"
                      >
                        {manualOpen ? "Hide manual entry" : "Show manual entry"}
                        <ChevronDown
                          size={13}
                          className={`transition-transform ${manualOpen ? "rotate-180" : ""
                            }`}
                        />
                      </button>
                    </div>

                    {manualOpen && (
                      <div className="mt-3 rounded-xl border border-[#d8eaff] bg-[#f8fbff] p-3">
                        <div className="mb-3 flex gap-2">
                          {["VIN", "Rego"].map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => setIdType(type)}
                              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${idType === type
                                  ? "bg-[#1287ee] text-white"
                                  : "bg-white text-[#667085] ring-1 ring-[#dce4ee]"
                                }`}
                            >
                              {type}
                            </button>
                          ))}
                        </div>

                        <input
                          type="text"
                          value={idValue}
                          onChange={(event) =>
                            setIdValue(event.target.value.toUpperCase())
                          }
                          placeholder={
                            idType === "VIN"
                              ? "Enter VIN number"
                              : "Enter registration"
                          }
                          className="h-10 w-full rounded-lg border border-[#dce4ee] bg-white px-3 text-xs outline-none focus:border-[#1287ee]"
                        />
                      </div>
                    )}
                  </div>
                </section>

                {/* Step 3 */}
                <section className="mt-10 border-t border-[#eceef1] pt-9 lg:mt-0 lg:min-h-[315px] lg:border-t-0 lg:pl-16 lg:pt-0 xl:pl-20">
                  <StepHeader
                    number="3"
                    title="What part do you need?"
                    tone="orange"
                  />

                  <div className="mt-8">
                    <button
                      type="button"
                      onClick={toggleRecording}
                      className={`flex min-h-[150px] w-full flex-col items-center justify-center rounded-[10px] border border-dashed px-5 py-5 text-center transition ${recording
                          ? "border-[#ff5a5f] bg-[#fff6f6]"
                          : "border-[#ffbf7e] bg-[#fffdfa] hover:bg-[#fff9f4]"
                        }`}
                    >
                      <div
                        className={`grid h-[52px] w-[52px] place-items-center rounded-full p-2 ${recording ? "bg-[#ffd8da]" : "bg-[#ffe9e9]"
                          }`}
                      >
                        <span
                          className={`grid h-10 w-10 place-items-center rounded-full text-white shadow-[0_0_0_5px_rgba(239,49,55,0.10)] ${recording
                              ? "animate-pulse bg-[#e61d24]"
                              : "bg-[#ef3137]"
                            }`}
                        >
                          {recording ? <Square size={17} /> : <Mic size={19} />}
                        </span>
                      </div>

                      <p className="mt-4 text-[13px] font-bold leading-[1.35] text-[#111a31]">
                        {recording
                          ? `Recording ${formatSeconds(recordSecs)} - tap to stop`
                          : voiceNotes.length > 0
                            ? "Tap to record another voice message"
                            : "Tap to record a voice message"}
                      </p>
                      <p className="mt-1 text-[10px] text-[#8b8f97]">
                        Tell us what part you need.
                      </p>
                    </button>

                    {voiceNotes.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {voiceNotes.map((voice, index) => (
                          <div
                            key={voice.url}
                            className="flex items-center gap-2 rounded-lg border border-[#f0e7e7] bg-white p-2"
                          >
                            <audio
                              controls
                              src={voice.url}
                              className="h-8 min-w-0 flex-1"
                            />
                            <button
                              type="button"
                              onClick={() => removeVoice(index)}
                              className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-[#8c929b] hover:bg-red-50 hover:text-red-600"
                              aria-label={`Remove voice note ${index + 1}`}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => partInputRef.current?.click()}
                        className="flex h-[58px] items-center justify-center gap-2 rounded-[10px] bg-[#f7f7f8] px-3 text-sm font-medium text-[#ff7900] transition hover:bg-[#f1f1f2]"
                      >
                        <Camera size={19} />
                        <span>
                          {partPhotos.length > 0
                            ? `${partPhotos.length} Photo${partPhotos.length > 1 ? "s" : ""}`
                            : "Add Photos"}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowNotes((current) => !current)}
                        className={`flex h-[58px] items-center justify-center gap-2 rounded-[10px] border px-3 text-sm font-medium transition ${showNotes || notes
                            ? "border-[#ff7900] bg-[#fffaf5] text-[#1f2430]"
                            : "border-[#ff8a2a] bg-white text-[#1f2430] hover:bg-[#fffaf5]"
                          }`}
                      >
                        <Pencil size={18} className="text-[#ff7900]" />
                        <span>{notes.trim() ? "Note Added" : "Add Note"}</span>
                      </button>
                    </div>

                    <input
                      ref={partInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      onChange={(event) => {
                        handlePartFiles(event.target.files);
                        event.target.value = "";
                      }}
                    />

                    {partPhotos.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {partPhotos.map((photo, index) => (
                          <div
                            key={photo.url}
                            className="relative h-14 w-14 overflow-visible"
                          >
                            <img
                              src={photo.url}
                              alt={`Part ${index + 1}`}
                              className="h-full w-full rounded-lg border border-[#e6e7e9] object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removePartPhoto(index)}
                              className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-[#182035] text-white"
                              aria-label={`Remove part photo ${index + 1}`}
                            >
                              <X size={11} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {(showNotes || notes) && (
                      <textarea
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        placeholder="Add note..."
                        rows={3}
                        className="mt-3 min-h-[68px] w-full resize-none rounded-[10px] border border-transparent bg-[#f7f7f8] px-4 py-3 text-xs text-[#242833] outline-none placeholder:text-[#c0c2c7] focus:border-[#ffd1ad] focus:bg-white"
                      />
                    )}
                  </div>
                </section>
              </div>

              <div className="mx-6 border-t border-[#e8e9ec] md:mx-10 lg:mx-16 xl:mx-20" />

              {error && (
                <div className="mx-auto mt-5 flex max-w-[760px] items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle size={17} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="px-6 pt-8 md:px-10 lg:px-16 xl:px-20">
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting || recording}
                  className="mx-auto flex h-[50px] w-full max-w-[490px] items-center justify-center rounded-full bg-[#0785ee] text-[13px] font-semibold text-white shadow-[0_8px_16px_rgba(7,133,238,0.18)] transition hover:bg-[#0076da] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Find My Part"}
                </button>
              </div>

              <div className="mx-auto mt-10 grid max-w-[1090px] grid-cols-1 gap-y-5 px-7 pb-10 sm:grid-cols-2 lg:grid-cols-4 lg:px-0 lg:pb-12">
                <TrustItem
                  icon={<Clock3 size={23} />}
                  title="Response within"
                  subtitle="10 minutes"
                  tone="blue"
                />
                <TrustItem
                  icon={<Truck size={24} />}
                  title="Australia"
                  subtitle="wide delivery"
                  tone="red"
                />
                <TrustItem
                  icon={<BadgeCheck size={24} />}
                  title="Quality"
                  subtitle="Tested Parts"
                  tone="green"
                />
                <TrustItem
                  icon={<ShieldCheck size={24} />}
                  title="Trusted by"
                  subtitle="2,000+ Mechanics"
                  tone="purple"
                />
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function StepHeader({ number, title, tone }) {
  const styles = {
    green: "bg-[#eafbf2] text-[#12a85a]",
    blue: "bg-[#eaf5ff] text-[#1684ea]",
    orange: "bg-[#fff1e2] text-[#f18615]",
  };

  return (
    <div className="flex items-center gap-4">
      <span
        className={`grid h-[29px] w-[29px] shrink-0 place-items-center rounded-full text-[12px] font-bold ${styles[tone]}`}
      >
        {number}
      </span>
      <h2 className="text-[17px] font-extrabold tracking-[-0.01em] text-[#141c30]">
        {title}
      </h2>
    </div>
  );
}

function TrustItem({ icon, title, subtitle, tone }) {
  const styles = {
    blue: "bg-[#f3f7ff] text-[#5f8ce6]",
    red: "bg-[#fff5f5] text-[#ff6a6a]",
    green: "bg-[#f1fbf6] text-[#55c893]",
    purple: "bg-[#f8f3ff] text-[#9c6be8]",
  };

  return (
    <div className="flex items-center gap-4 px-4 lg:border-r lg:border-[#dfe2e6] lg:last:border-r-0 lg:px-8">
      <span
        className={`grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full ${styles[tone]}`}
      >
        {icon}
      </span>
      <div className="text-[12px] leading-[1.35] text-[#14213b]">
        <div className="font-semibold">{title}</div>
        <div className="font-semibold">{subtitle}</div>
      </div>
    </div>
  );
}

function SuccessPanel({ request, name, contact, onAnother }) {
  return (
    <div className="mx-auto max-w-2xl px-6 py-20 text-center sm:px-10">
      <CheckCircle2 size={60} className="mx-auto text-emerald-500" />
      <h2 className="mt-5 text-3xl font-extrabold text-[#151d31]">
        Request sent{name ? `, ${name.split(" ")[0]}` : ""}
      </h2>

      {request?.requestId && (
        <p className="mt-3 text-sm text-[#687080]">
          Your reference is{" "}
          <strong className="text-[#151d31]">{request.requestId}</strong>
        </p>
      )}

      <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-[#687080]">
        We&apos;ll contact you on <strong>{contact}</strong> with your quote,
        usually within 10 minutes during business hours.
      </p>

      <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
        <button
          type="button"
          onClick={onAnother}
          className="rounded-full bg-[#0785ee] px-7 py-3 text-sm font-semibold text-white hover:bg-[#0076da]"
        >
          Request another part
        </button>
        <Link
          to="/Register"
          className="rounded-full border border-[#d9dde4] px-7 py-3 text-sm font-semibold text-[#394150] hover:bg-[#f7f8fa]"
        >
          Create an account
        </Link>
      </div>
    </div>
  );
}
